import {
  type CarryForwardInput,
  CarryForwardInputSchema,
  type CarryForwardOutput,
  type CarryForwardRules,
  type CoContributionInput,
  CoContributionInputSchema,
  type CoContributionOutput,
  type CoContributionRules,
  type SacrificeInput,
  SacrificeInputSchema,
  type SacrificeOutput,
  type SpouseOffsetInput,
  SpouseOffsetInputSchema,
  type SpouseOffsetOutput,
  type SpouseOffsetRules,
  type SuperBoostRuleSet,
} from "./types.js";

// ---------------------------------------------------------------------------
// HARD RULE (binding, panel C2): superannuation is an s764A financial product
// with NO carve-out. No fund / super-product / SMSF / platform / "boost your
// super" identifier may ever reach a personalised-result output from this
// surface — affiliate revenue is $0 by design. We encode that as a guard run
// over every emitted output object, not as doc prose. Any string field that
// smells like a financial-product / fund handoff trips the guard and the calc
// throws — a casual later "add a fund tile to the result" becomes a loud test
// failure, not a silent breach.
// ---------------------------------------------------------------------------

/** Tokens that signal a super-product / fund / handoff has leaked into a result. */
const FUND_HANDOFF_TOKENS = [
  "fund",
  "smsf",
  "industry super",
  "retail super",
  "open an account",
  "sign up",
  "join now",
  "switch to",
  "rollover to",
  "affiliate",
  "sponsored",
  "partner offer",
  "apply now",
  "compare funds",
  "best super",
  "usi", // unique superannuation identifier
  "abn:",
  "spin:", // superannuation product identification number
];

/**
 * Guard: assert no fund/super-product identifier or handoff CTA can reach a
 * personalised-result output. Walks every string value in the output object and
 * throws if any token matches. Pure; returns the output unchanged when clean so
 * it can wrap a `return`.
 */
export function assertNoFundHandoff<T>(output: T): T {
  const visit = (value: unknown): void => {
    if (typeof value === "string") {
      const haystack = value.toLowerCase();
      for (const token of FUND_HANDOFF_TOKENS) {
        if (haystack.includes(token)) {
          throw new Error(
            `super-boost: blocked fund/super-product handoff in a personalised result (matched "${token}"). Superannuation is an s764A financial product — no fund handoff may reach this surface (panel C2).`,
          );
        }
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (value && typeof value === "object") {
      for (const v of Object.values(value)) visit(v);
    }
  };
  visit(output);
  return output;
}

// ---------------------------------------------------------------------------
// coContribution
// ---------------------------------------------------------------------------

/**
 * Government super co-contribution. The entitlement amount is the lesser of the
 * income-test maximum (the $500 max tapered by total income) and the
 * matching-rate cap on the eligible personal contribution. The eligibility gate
 * is reported separately as a set of booleans — the AMOUNT is shown regardless
 * so the UI can say "you would qualify for $X if these conditions are met"
 * (conditions-before-number, mechanism-not-recommendation).
 */
export function coContribution(
  rawInput: CoContributionInput,
  rules: SuperBoostRuleSet,
): CoContributionOutput {
  const input = CoContributionInputSchema.parse(rawInput);
  const r = rules.coContribution;
  const e = input.eligibility;

  const incomeTestMax = coContributionIncomeTestMax(r, input.totalIncome);
  // Matching cap: 50c per $1 of eligible contribution.
  const contributionCap = Math.round(input.personalContribution * r.matchingRate);
  const entitlement = Math.min(incomeTestMax, contributionCap);

  const failed: string[] = [];
  if (!e.madeNonConcessionalContribution) failed.push("nonConcessionalContribution");
  if (!e.tenPercentEligibleIncome) failed.push("tenPercentEligibleIncome");
  if (e.ageAtYearEnd >= r.maxAgeExclusive) failed.push("ageUnder71");
  if (e.totalSuperBalance >= r.tsbCap) failed.push("tsbBelowCap");
  if (!e.lodgesReturn) failed.push("lodgesReturn");
  if (e.disqualifyingTempVisa) failed.push("noDisqualifyingVisa");

  const eligible = failed.length === 0;

  return assertNoFundHandoff({
    totalIncome: input.totalIncome,
    personalContribution: input.personalContribution,
    eligible,
    failedConditions: failed,
    coContribution: eligible ? entitlement : 0,
    incomeTestMax,
  });
}

/** The income-test maximum: full max at/below lower threshold, tapered to nil at
 * the higher threshold, nil at/above it. Whole dollars. */
export function coContributionIncomeTestMax(
  r: CoContributionRules,
  totalIncome: number,
): number {
  if (totalIncome <= r.lowerThreshold) return r.maxAmount;
  if (totalIncome >= r.higherThreshold) return 0;
  const reduced = r.maxAmount - (totalIncome - r.lowerThreshold) * r.taperRate;
  return Math.max(0, Math.round(reduced));
}

// ---------------------------------------------------------------------------
// spouseOffset
// ---------------------------------------------------------------------------

/**
 * Spouse-contribution tax offset: 18% of the lesser of (a) the $3,000 base
 * reduced $1-for-$1 once spouse income exceeds $37,000, and (b) the actual
 * contribution. Nil once spouse income reaches $40,000.
 */
export function spouseOffset(
  rawInput: SpouseOffsetInput,
  rules: SuperBoostRuleSet,
): SpouseOffsetOutput {
  const input = SpouseOffsetInputSchema.parse(rawInput);
  const r = rules.spouseOffset;

  const withinCutOut = input.spouseIncome < r.cutOutThreshold;
  // Base after the $1-for-$1 taper over the full-offset threshold.
  const taperedBase =
    input.spouseIncome <= r.fullOffsetThreshold
      ? r.maxContributionBase
      : Math.max(
          0,
          r.maxContributionBase - (input.spouseIncome - r.fullOffsetThreshold),
        );
  const eligibleBase = Math.min(taperedBase, input.contribution);
  const offset = Math.round(eligibleBase * r.offsetRate);

  return assertNoFundHandoff({
    spouseIncome: input.spouseIncome,
    contribution: input.contribution,
    eligibleBase,
    offset,
    withinCutOut,
  });
}

// ---------------------------------------------------------------------------
// carryForwardHeadroom
// ---------------------------------------------------------------------------

/** Parse a FY key like "2021-22" into its starting calendar year (2021). */
function fyStartYear(fy: string): number {
  const start = Number.parseInt(fy.slice(0, 4), 10);
  if (Number.isNaN(start)) {
    throw new Error(`super-boost: malformed financial-year key "${fy}"`);
  }
  return start;
}

/** Build a FY key "YYYY-YY" from a starting calendar year (2021 → "2021-22"). */
function fyKey(startYear: number): string {
  const end = (startYear + 1) % 100;
  return `${startYear}-${end.toString().padStart(2, "0")}`;
}

/**
 * Unused concessional-cap carry-forward headroom under the 5-year rolling
 * window. Keeps only the years strictly INSIDE the window for the assessment FY
 * (the 5 previous FYs — the current FY's own cap is not "carried forward"), sums
 * the unused slices, gates USE on the $500k prior-year TSB, and reports the
 * oldest in-window slice as a calm dated fact (no urgency UI). The
 * marginal-vs-15% tax saving is illustrative mechanics, not a "save $X" claim.
 */
export function carryForwardHeadroom(
  rawInput: CarryForwardInput,
  rules: SuperBoostRuleSet,
): CarryForwardOutput {
  const input = CarryForwardInputSchema.parse(rawInput);
  const r = rules.carryForward;

  const assessStart = fyStartYear(input.assessmentYear);
  // The rolling window is the `windowYears` financial years immediately before
  // the assessment FY: [assessStart - windowYears, assessStart - 1].
  const windowOldestStart = assessStart - r.windowYears;
  const firstAccrualStart = fyStartYear(r.firstAccrualFy);

  // An unused slice from FY starting year `s` expires at the END of the FY
  // starting `s + windowYears` (i.e. it is usable in s+1..s+windowYears).
  const expiresThisFy = (sliceStart: number): boolean =>
    sliceStart + r.windowYears === assessStart;

  const perYear = [...input.history]
    .map((y) => ({ ...y, start: fyStartYear(y.fy) }))
    .filter(
      (y) =>
        y.start >= windowOldestStart &&
        y.start <= assessStart - 1 &&
        y.start >= firstAccrualStart,
    )
    .sort((a, b) => a.start - b.start)
    .map((y) => ({
      fy: y.fy,
      cap: y.cap,
      contributions: y.contributions,
      unused: Math.max(0, y.cap - y.contributions),
      expiresThisFy: expiresThisFy(y.start),
    }));

  const availableHeadroom = perYear.reduce((sum, y) => sum + y.unused, 0);
  const canUse = input.priorYearTsb < r.tsbGate;

  const oldestWithUnused = perYear.find((y) => y.unused > 0) ?? null;
  const oldestExpiring = oldestWithUnused
    ? {
        fy: oldestWithUnused.fy,
        unused: oldestWithUnused.unused,
        // Expires at the end of the FY starting (sliceStart + windowYears).
        expiresEndOfFy: fyKey(fyStartYear(oldestWithUnused.fy) + r.windowYears),
      }
    : null;

  // Illustrative differential: marginal − 15% in-fund, on the usable headroom.
  const concessionPerDollar = Math.max(
    0,
    input.marginalRate - rules.concessionalTaxRate,
  );
  const taxSavingIfContributed = canUse
    ? Math.round(availableHeadroom * concessionPerDollar)
    : 0;

  return assertNoFundHandoff({
    availableHeadroom,
    perYear,
    canUse,
    oldestExpiring,
    taxSavingIfContributed,
  });
}

// ---------------------------------------------------------------------------
// sacrificeTaxSaving
// ---------------------------------------------------------------------------

/**
 * Salary-sacrifice / personal-deductible concession = marginal − 15% per $1.
 *
 * LOW-INCOME HONESTY (binding, sacrifice ledger #6 correction): the concession
 * is small-but-POSITIVE in the 16% bracket (16% − 15% = 1%, or 18% − 15% = 3%
 * once 2% Medicare applies above the Medicare low-income threshold) and does
 * NOT invert (go negative) anywhere AT OR ABOVE the $18,200 tax-free threshold —
 * 16% > 15% always. It can only invert below $18,200, where the marginal rate
 * is 0% and the 15% in-fund tax is a genuine cost with no cash-tax offset; LISTO
 * then refunds that 15% up to the LISTO income threshold. The caller supplies
 * the with-Medicare marginal rate (derived from the income-tax engine).
 */
export function sacrificeTaxSaving(
  rawInput: SacrificeInput,
  rules: SuperBoostRuleSet,
): SacrificeOutput {
  const input = SacrificeInputSchema.parse(rawInput);
  const concessionalTaxRate = rules.concessionalTaxRate;

  const concessionPerDollar = input.marginalRate - concessionalTaxRate;
  const taxSaving = Math.round(input.contribution * concessionPerDollar);
  const concessionInverts = concessionPerDollar <= 0;

  let listo: number | null = null;
  if (input.adjustedTaxableIncome !== undefined) {
    const l = rules.listo;
    listo =
      input.adjustedTaxableIncome <= l.incomeThreshold
        ? Math.min(l.max, Math.round(input.contribution * l.rate))
        : 0;
  }

  return assertNoFundHandoff({
    contribution: input.contribution,
    marginalRate: input.marginalRate,
    concessionalTaxRate,
    concessionPerDollar,
    taxSaving,
    concessionInverts,
    listo,
  });
}
